(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-ll-savoir-film]';
  const INSTANCE_KEY = '__llSavoirFilmProduction';
  const MANAGER_KEY = '__LLCL_SAVOIR_FILM_PRODUCTION_MANAGER__';

  if (window[MANAGER_KEY]) {
    window[MANAGER_KEY].init(document);
    return;
  }

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const number = (value, fallback = 0) => {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };
  const pad = (value) => String(value).padStart(2, '0');

  class LLSavoirFilmV7 {
    constructor(root) {
      this.root = root;
      this.sticky = root.querySelector('[data-ll-sf-sticky]');
      this.scrollSpace = root.querySelector('[data-ll-sf-scroll-space]');
      this.video = root.querySelector('.ll-savoir-film__video');
      this.heading = root.querySelector('[data-ll-sf-heading]');
      this.cardTrack = root.querySelector('[data-ll-sf-card-track]');
      this.chapterDock = root.querySelector('[data-ll-sf-chapters]');
      this.chapters = Array.from(root.querySelectorAll('[data-ll-sf-chapter]'));
      this.desktopButtons = Array.from(root.querySelectorAll('[data-ll-sf-chapter-jump]'));
      this.mobileButtons = Array.from(root.querySelectorAll('[data-ll-sf-rail-jump]'));
      this.desktopLines = Array.from(root.querySelectorAll('[data-ll-sf-desktop-line]'));
      this.mobileSegments = Array.from(root.querySelectorAll('[data-ll-sf-rail-segment]'));
      this.currentLabels = Array.from(root.querySelectorAll('[data-ll-sf-current]'));
      this.activeCode = root.querySelector('[data-ll-sf-active-code]');
      this.playButton = root.querySelector('[data-ll-sf-play]');
      this.playIcon = root.querySelector('[data-ll-sf-play-icon]');
      this.playLabel = root.querySelector('[data-ll-sf-play-label]');
      this.soundButton = root.querySelector('[data-ll-sf-sound]');
      this.soundLabel = root.querySelector('[data-ll-sf-sound-label]');

      this.mobileQuery = window.matchMedia('(max-width: 749px)');
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.autoplay = root.dataset.autoplay === 'true';
      this.segmentLoop = root.dataset.segmentLoop !== 'false';
      this.activeIndex = -1;
      this.inViewport = false;
      this.manualPause = false;
      this.editorHold = false;
      this.destroyed = false;
      this.frame = null;
      this.measureFrame = null;
      this.headingTimer = null;
      this.viewportObserver = null;
      this.resizeObserver = null;
      this.chapterStarts = this.buildChapterStarts();
      this.cardOffsets = [];
      this.cardHeights = [];

      const computed = getComputedStyle(root);
      this.mobileBaseScale = number(computed.getPropertyValue('--sf-video-scale-mobile'), 1.18);
      this.railNumberInactive = number(computed.getPropertyValue('--sf-mobile-rail-number-inactive'), 11);
      this.railNumberActive = number(computed.getPropertyValue('--sf-mobile-rail-number-active'), 20);

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onResponsiveChange = this.onResponsiveChange.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onVideoMetadata = this.onVideoMetadata.bind(this);
      this.onVideoTime = this.onVideoTime.bind(this);
      this.onVideoPlay = this.onVideoPlay.bind(this);
      this.onVideoPause = this.onVideoPause.bind(this);

      this.init();
    }

    init() {
      if (!this.sticky || !this.scrollSpace || this.chapters.length === 0) return;

      this.root.dataset.llSavoirProduction = 'true';
      this.initNavigation();
      this.initVideo();
      this.initViewportObserver();
      this.initResizeObserver();

      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize, { passive: true });
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onResponsiveChange);
      } else {
        this.mobileQuery.addListener(this.onResponsiveChange);
      }

      this.activateChapter(0, { force: true, seek: false, immediateHeading: true });
      this.measureMobileLayout();
      this.updatePlaybackUI();
      this.updateSoundUI();
      this.requestUpdate();
    }

    buildChapterStarts() {
      if (!this.chapters.length) return [];
      let previous = -1;
      return this.chapters.map((chapter, index) => {
        const fallback = (index / this.chapters.length) * 100;
        let value = clamp(number(chapter.dataset.startPercent, fallback), 0, 99);
        if (index === 0) value = 0;
        if (index > 0) value = Math.max(value, previous + 1);
        previous = clamp(value, 0, 99);
        return previous;
      });
    }

    onScroll() {
      this.requestUpdate();
    }

    refreshTokens() {
      const computed = getComputedStyle(this.root);
      this.mobileBaseScale = number(computed.getPropertyValue('--sf-video-scale-mobile'), this.mobileBaseScale);
      this.railNumberInactive = number(computed.getPropertyValue('--sf-mobile-rail-number-inactive'), this.railNumberInactive);
      this.railNumberActive = number(computed.getPropertyValue('--sf-mobile-rail-number-active'), this.railNumberActive);
    }

    onResize() {
      this.refreshTokens();
      this.scheduleMobileMeasure();
      this.requestUpdate();
    }

    onResponsiveChange() {
      this.refreshTokens();
      if (this.mobileQuery.matches) {
        this.scheduleMobileMeasure();
      } else {
        this.root.style.removeProperty('--sf-mobile-track-y');
        this.root.style.removeProperty('--sf-mobile-dock-h-live');
      }
      this.requestUpdate();
    }

    requestUpdate() {
      if (this.frame !== null) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.updateFromScroll();
      });
    }

    getStickyTop() {
      return number(getComputedStyle(this.sticky).top, 0);
    }

    getScrollMetrics() {
      const rect = this.scrollSpace.getBoundingClientRect();
      const stickyHeight = this.sticky.getBoundingClientRect().height;
      const travel = Math.max(this.scrollSpace.offsetHeight - stickyHeight, 1);
      const stickyTop = this.getStickyTop();
      const travelled = clamp(stickyTop - rect.top, 0, travel);
      return {
        rect,
        stickyTop,
        stickyHeight,
        travel,
        progress: clamp(travelled / travel, 0, 1)
      };
    }

    updateFromScroll() {
      if (this.destroyed) return;
      const metrics = this.getScrollMetrics();
      const progress = metrics.progress;
      this.root.style.setProperty('--sf-scroll-progress', progress.toFixed(5));

      if (this.mobileQuery.matches) {
        this.updateMobile(progress);
      } else {
        this.updateDesktop(progress);
      }

    }

    updateDesktop(progress) {
      const count = this.chapters.length;
      const index = progress >= 1
        ? count - 1
        : clamp(Math.floor(progress * count), 0, count - 1);

      if (!this.editorHold) this.activateChapter(index, { seek: true });

      const phase = progress * Math.max(count - 1, 1);
      this.desktopLines.forEach((line, lineIndex) => {
        const fill = line.querySelector('span');
        if (!fill) return;
        fill.style.transform = `scaleX(${clamp(phase - lineIndex, 0, 1)})`;
      });

      this.root.style.removeProperty('--sf-mobile-card-progress');
      this.root.style.removeProperty('--sf-mobile-motion-y');
      this.root.style.removeProperty('--sf-mobile-video-scale-live');
    }

    scheduleMobileMeasure() {
      if (!this.mobileQuery.matches || this.measureFrame !== null) return;
      this.measureFrame = requestAnimationFrame(() => {
        this.measureFrame = null;
        this.measureMobileLayout();
      });
    }

    measureMobileLayout() {
      if (!this.cardTrack || !this.chapters.length || !this.mobileQuery.matches) return;

      this.cardOffsets = this.chapters.map((chapter) => chapter.offsetTop);
      this.cardHeights = this.chapters.map((chapter) => chapter.offsetHeight);

      const maxHeight = Math.max(...this.cardHeights, 1);
      this.root.style.setProperty('--sf-mobile-dock-h-live', `${maxHeight.toFixed(2)}px`);

      const position = number(this.root.style.getPropertyValue('--sf-mobile-card-progress'), 0);
      this.setMobileCardPosition(position);
    }

    setMobileCardPosition(position) {
      const count = this.chapters.length;
      if (!count || !this.cardTrack) return;

      if (this.cardOffsets.length !== count || this.cardHeights.length !== count) {
        this.cardOffsets = this.chapters.map((chapter) => chapter.offsetTop);
        this.cardHeights = this.chapters.map((chapter) => chapter.offsetHeight);
      }

      const bounded = clamp(position, 0, Math.max(count - 1, 0));
      const lower = Math.floor(bounded);
      const upper = Math.min(lower + 1, count - 1);
      const local = bounded - lower;

      const yA = this.cardOffsets[lower] ?? 0;
      const yB = this.cardOffsets[upper] ?? yA;
      const trackY = -(yA + ((yB - yA) * local));

      this.root.style.setProperty('--sf-mobile-track-y', `${trackY.toFixed(3)}px`);
    }

    resolveMobileActiveIndex(position) {
      const count = this.chapters.length;
      if (!count) return 0;

      let index = clamp(this.activeIndex >= 0 ? this.activeIndex : Math.round(position), 0, count - 1);
      const threshold = 0.58;

      while (index < count - 1 && position >= index + threshold) index += 1;
      while (index > 0 && position <= index - threshold) index -= 1;

      return index;
    }

    updateMobile(progress) {
      const count = this.chapters.length;
      const maxPosition = Math.max(count - 1, 0);
      const position = progress * maxPosition;
      const activeIndex = this.resolveMobileActiveIndex(position);

      this.root.style.setProperty('--sf-mobile-card-progress', position.toFixed(5));
      this.setMobileCardPosition(position);

      this.mobileSegments.forEach((segment, index) => {
        const fill = segment.querySelector('span');
        if (!fill) return;
        fill.style.transform = `scaleY(${clamp(position - index, 0, 1)})`;
      });

      const maxNumberScale = Math.max(this.railNumberActive / Math.max(this.railNumberInactive, 1), 1);

      this.mobileButtons.forEach((button, index) => {
        const distance = Math.abs(position - index);
        const influence = 1 - clamp(distance, 0, 1);
        const numberScale = 1 + ((maxNumberScale - 1) * influence);
        const dotScale = 1 + (0.85 * influence);

        button.style.setProperty('--sf-point-influence', influence.toFixed(4));
        button.style.setProperty('--sf-point-opacity', (0.46 + (0.54 * influence)).toFixed(4));
        button.style.setProperty('--sf-point-fill', influence.toFixed(4));
        button.style.setProperty('--sf-point-glow', `${(7 * influence).toFixed(2)}px`);
        button.style.setProperty('--sf-number-scale', numberScale.toFixed(4));
        button.style.setProperty('--sf-dot-scale', dotScale.toFixed(4));
      });

      // Keep motion perceptible but compositor-only and deliberately restrained.
      const liveScale = this.mobileBaseScale + (0.003 * progress);
      const motionY = -3 * progress;
      this.root.style.setProperty('--sf-mobile-video-scale-live', liveScale.toFixed(4));
      this.root.style.setProperty('--sf-mobile-motion-y', `${motionY.toFixed(2)}px`);

      if (!this.editorHold) this.activateChapter(activeIndex, { seek: true });
    }

    activateChapter(index, options = {}) {
      const { force = false, seek = true, immediateHeading = false } = options;
      if (index < 0 || index >= this.chapters.length) return;
      const changed = index !== this.activeIndex;
      if (!changed && !force) return;

      this.activeIndex = index;
      const chapter = this.chapters[index];

      this.chapters.forEach((item, itemIndex) => {
        const active = itemIndex === index;
        item.classList.toggle('is-active', active);
        item.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      [...this.desktopButtons, ...this.mobileButtons].forEach((button) => {
        const buttonIndex = number(
          button.dataset.llSfChapterJump ?? button.dataset.llSfRailJump,
          -1
        );
        const active = buttonIndex === index;
        button.classList.toggle('is-active', active);
        if (active) button.setAttribute('aria-current', 'true');
        else button.removeAttribute('aria-current');
      });

      this.currentLabels.forEach((label) => { label.textContent = pad(index + 1); });

      if (this.activeCode) {
        const metaCode = chapter.dataset.metaCode || `${String.fromCharCode(65 + index)}.${pad(index + 1)}`;
        this.activeCode.textContent = metaCode;
      }

      const displayHeading = chapter.dataset.displayHeading || '';
      this.changeHeading(displayHeading, immediateHeading);

      if (changed && seek) this.seekToChapter(index);
      this.root.dataset.activeChapter = String(index + 1);
    }

    changeHeading(text, immediate = false) {
      if (!this.heading || !text) return;
      const next = String(text).trim();
      if (!next || this.heading.textContent.trim() === next) return;

      clearTimeout(this.headingTimer);

      if (immediate || this.reducedMotionQuery.matches) {
        this.heading.textContent = next;
        this.heading.classList.remove('is-changing');
        return;
      }

      this.heading.classList.add('is-changing');
      this.headingTimer = window.setTimeout(() => {
        this.heading.textContent = next;
        requestAnimationFrame(() => this.heading.classList.remove('is-changing'));
      }, 110);
    }

    initNavigation() {
      const buttons = [...this.desktopButtons, ...this.mobileButtons];
      buttons.forEach((button) => {
        const index = number(
          button.dataset.llSfChapterJump ?? button.dataset.llSfRailJump,
          -1
        );
        if (index < 0) return;
        const handler = () => this.goToChapter(index);
        button.addEventListener('click', handler);
        button.__llSfV7Handler = handler;
      });
    }

    goToChapter(index) {
      if (index < 0 || index >= this.chapters.length) return;
      const metrics = this.getScrollMetrics();
      const count = this.chapters.length;
      const targetProgress = this.mobileQuery.matches
        ? (count <= 1 ? 0 : index / (count - 1))
        : (index + 0.5) / count;

      const absoluteTop = window.scrollY + metrics.rect.top;
      const target = absoluteTop - metrics.stickyTop + (metrics.travel * targetProgress);

      this.activateChapter(index, { seek: true });
      window.scrollTo({
        top: Math.max(target, 0),
        behavior: this.reducedMotionQuery.matches ? 'auto' : 'smooth'
      });
    }

    initVideo() {
      if (!this.video) return;
      this.video.muted = true;
      this.video.playsInline = true;

      this.video.addEventListener('loadedmetadata', this.onVideoMetadata);
      this.video.addEventListener('timeupdate', this.onVideoTime);
      this.video.addEventListener('play', this.onVideoPlay);
      this.video.addEventListener('pause', this.onVideoPause);

      if (this.playButton) {
        this.playHandler = () => {
          if (this.video.paused) {
            this.manualPause = false;
            this.safePlay(true);
          } else {
            this.manualPause = true;
            this.video.pause();
          }
        };
        this.playButton.addEventListener('click', this.playHandler);
      }

      if (this.soundButton) {
        this.soundHandler = () => {
          this.video.muted = !this.video.muted;
          this.updateSoundUI();
        };
        this.soundButton.addEventListener('click', this.soundHandler);
      }

      if (this.video.readyState >= 1) this.onVideoMetadata();
    }

    getSegment(index) {
      if (!this.video || !Number.isFinite(this.video.duration) || this.video.duration <= 0) return null;
      const startPercent = this.chapterStarts[index] ?? 0;
      const endPercent = this.chapterStarts[index + 1] ?? 100;
      const start = this.video.duration * (startPercent / 100);
      const end = Math.min(
        this.video.duration,
        Math.max(this.video.duration * (endPercent / 100), start + 0.5)
      );
      return { start, end };
    }

    seekToChapter(index) {
      if (!this.video) return;
      const segment = this.getSegment(index);
      if (!segment) return;

      try {
        this.video.currentTime = Math.min(segment.start + 0.08, segment.end - 0.08);
      } catch (_) {
        return;
      }

      if (
        this.autoplay &&
        this.inViewport &&
        !this.manualPause &&
        !this.editorHold &&
        !this.reducedMotionQuery.matches &&
        !document.hidden
      ) {
        this.safePlay(false);
      }
    }

    onVideoMetadata() {
      this.chapterStarts = this.buildChapterStarts();
      this.cardOffsets = [];
      this.cardHeights = [];
      this.scheduleMobileMeasure();
      if (this.activeIndex >= 0) this.seekToChapter(this.activeIndex);
    }

    onVideoTime() {
      if (!this.video || this.activeIndex < 0) return;
      const segment = this.getSegment(this.activeIndex);
      if (!segment) return;

      if (this.video.currentTime >= segment.end - 0.16) {
        if (this.segmentLoop) {
          this.video.currentTime = segment.start + 0.08;
          if (!this.manualPause && this.inViewport && !this.editorHold) this.safePlay(false);
        } else {
          this.video.pause();
        }
      }
    }

    async safePlay(userInitiated = false) {
      if (!this.video || document.hidden || this.destroyed) return;
      if (!userInitiated) {
        if (
          !this.autoplay ||
          !this.inViewport ||
          this.manualPause ||
          this.editorHold ||
          this.reducedMotionQuery.matches
        ) return;
      }

      try {
        const promise = this.video.play();
        if (promise && typeof promise.then === 'function') await promise;
      } catch (_) {
        this.updatePlaybackUI();
      }
    }

    onVideoPlay() {
      this.updatePlaybackUI();
    }

    onVideoPause() {
      this.updatePlaybackUI();
    }

    updatePlaybackUI() {
      if (!this.video) return;
      const paused = this.video.paused;
      if (this.playLabel) this.playLabel.textContent = paused ? 'PLAY' : 'PAUSE';
      if (this.playIcon) this.playIcon.textContent = paused ? '▶' : 'II';
      if (this.playButton) this.playButton.setAttribute('aria-label', paused ? 'Reproducir film' : 'Pausar film');
    }

    updateSoundUI() {
      if (!this.video) return;
      if (this.soundLabel) this.soundLabel.textContent = this.video.muted ? 'SOUND +' : 'SOUND —';
      if (this.soundButton) this.soundButton.setAttribute('aria-pressed', this.video.muted ? 'false' : 'true');
    }

    initViewportObserver() {
      if (!('IntersectionObserver' in window)) {
        this.inViewport = true;
        return;
      }

      this.viewportObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          this.inViewport = entry.isIntersecting && entry.intersectionRatio > 0.04;
          if (this.inViewport) {
            if (this.autoplay && !this.manualPause && !this.editorHold) this.safePlay(false);
          } else if (this.video && !this.video.paused) {
            this.video.pause();
          }
        });
      }, { threshold: [0, 0.05, 0.2] });

      this.viewportObserver.observe(this.sticky);
    }

    initResizeObserver() {
      if (!('ResizeObserver' in window)) return;
      this.resizeObserver = new ResizeObserver(() => {
        this.scheduleMobileMeasure();
        this.requestUpdate();
      });
      this.resizeObserver.observe(this.sticky);
      this.resizeObserver.observe(this.scrollSpace);
      if (this.cardTrack) this.resizeObserver.observe(this.cardTrack);
      this.chapters.forEach((chapter) => this.resizeObserver.observe(chapter));

      if (document.fonts?.ready) {
        document.fonts.ready.then(() => {
          if (this.destroyed) return;
          this.scheduleMobileMeasure();
          this.requestUpdate();
        });
      }
    }

    onVisibilityChange() {
      if (!this.video) return;
      if (document.hidden) {
        this.video.pause();
      } else if (this.autoplay && this.inViewport && !this.manualPause && !this.editorHold) {
        this.safePlay(false);
      }
    }

    previewChapter(index) {
      this.editorHold = true;
      this.activateChapter(index, { force: true, seek: true, immediateHeading: true });
      if (this.mobileQuery.matches) {
        this.root.style.setProperty('--sf-mobile-card-progress', String(index));
        this.setMobileCardPosition(index);
      }
      if (this.video && !this.video.paused) this.video.pause();
    }

    releaseEditorHold() {
      this.editorHold = false;
      this.requestUpdate();
    }

    destroy() {
      if (this.destroyed) return;
      this.destroyed = true;

      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onResponsiveChange);
      } else {
        this.mobileQuery.removeListener(this.onResponsiveChange);
      }

      if (this.viewportObserver) this.viewportObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.frame !== null) cancelAnimationFrame(this.frame);
      if (this.measureFrame !== null) cancelAnimationFrame(this.measureFrame);
      clearTimeout(this.headingTimer);

      [...this.desktopButtons, ...this.mobileButtons].forEach((button) => {
        if (button.__llSfV7Handler) {
          button.removeEventListener('click', button.__llSfV7Handler);
          delete button.__llSfV7Handler;
        }
      });

      if (this.video) {
        this.video.removeEventListener('loadedmetadata', this.onVideoMetadata);
        this.video.removeEventListener('timeupdate', this.onVideoTime);
        this.video.removeEventListener('play', this.onVideoPlay);
        this.video.removeEventListener('pause', this.onVideoPause);
      }

      if (this.playButton && this.playHandler) this.playButton.removeEventListener('click', this.playHandler);
      if (this.soundButton && this.soundHandler) this.soundButton.removeEventListener('click', this.soundHandler);

      delete this.root[INSTANCE_KEY];
    }
  }

  const manager = {
    init(scope = document) {
      if (!scope) return;
      if (scope.matches?.(ROOT_SELECTOR)) this.initRoot(scope);
      scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => this.initRoot(root));
    },

    initRoot(root) {
      if (!root?.isConnected) return null;
      if (root[INSTANCE_KEY]) return root[INSTANCE_KEY];

      ['__llSavoirFilmV52', '__llSavoirFilmV6', '__llSavoirFilmV7'].forEach((key) => {
        const legacy = root[key];
        if (legacy?.destroy) {
          try { legacy.destroy(); } catch (_) {}
        }
        try { delete root[key]; } catch (_) {}
      });

      const instance = new LLSavoirFilmV7(root);
      root[INSTANCE_KEY] = instance;
      return instance;
    },

    destroy(scope) {
      const roots = [];
      if (scope?.matches?.(ROOT_SELECTOR)) roots.push(scope);
      scope?.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));
      roots.forEach((root) => root[INSTANCE_KEY]?.destroy?.());
    },

    fromEvent(event) {
      const root = event.target?.closest?.(ROOT_SELECTOR);
      if (root) return root[INSTANCE_KEY] || this.initRoot(root);

      const sectionId = event.detail?.sectionId;
      if (!sectionId) return null;
      const match = Array.from(document.querySelectorAll(ROOT_SELECTOR))
        .find((item) => item.dataset.sectionId === String(sectionId));
      return match ? (match[INSTANCE_KEY] || this.initRoot(match)) : null;
    },

    chapterIndexFromEvent(instance, event) {
      const chapter = event.target?.closest?.('[data-ll-sf-chapter]');
      if (chapter) return instance.chapters.indexOf(chapter);

      const blockId = event.detail?.blockId;
      if (!blockId) return -1;
      return instance.chapters.findIndex((item) =>
        (item.getAttribute('data-shopify-editor-block') || '').includes(String(blockId))
      );
    }
  };

  window[MANAGER_KEY] = manager;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => manager.init(document), { once: true });
  } else {
    manager.init(document);
  }

  document.addEventListener('shopify:section:load', (event) => manager.init(event.target));
  document.addEventListener('shopify:section:unload', (event) => manager.destroy(event.target));
  document.addEventListener('shopify:section:select', (event) => manager.fromEvent(event)?.requestUpdate());

  document.addEventListener('shopify:block:select', (event) => {
    const instance = manager.fromEvent(event);
    if (!instance) return;
    const index = manager.chapterIndexFromEvent(instance, event);
    if (index >= 0) instance.previewChapter(index);
  });

  document.addEventListener('shopify:block:deselect', (event) => {
    manager.fromEvent(event)?.releaseEditorHold();
  });
})();
